import React, { useState, useCallback } from 'react';
import { T } from '../../lib/tokens';

type Variant = 'sage' | 'navy' | 'cream' | 'ghostDark' | 'ghostLight' | 'delete';
type Size = 'default' | 'large' | 'small' | 'pill';

interface ButtonProps {
  children: React.ReactNode;
  variant?: Variant;
  size?: Size;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  type?: 'button' | 'submit' | 'reset';
}

const variantStyles: Record<Variant, React.CSSProperties> = {
  sage: {
    background: T.sage,
    color: T.navy,
    border: 'none',
  },
  navy: {
    background: T.navy,
    color: T.cream,
    border: 'none',
  },
  cream: {
    background: T.cream,
    color: T.navy,
    border: `1px solid ${T.paperLine}`,
  },
  ghostDark: {
    background: 'transparent',
    color: T.cream,
    border: `1px solid ${T.navyLine2}`,
  },
  ghostLight: {
    background: 'transparent',
    color: T.navy,
    border: `1px solid ${T.paperLine}`,
  },
  delete: {
    background: T.failBg,
    color: T.fail,
    border: 'none',
  },
};

const sizeStyles: Record<Size, React.CSSProperties> = {
  default: {
    padding: '10px 20px',
    fontSize: 14,
    borderRadius: 8,
  },
  large: {
    padding: '14px 28px',
    fontSize: 15,
    borderRadius: 10,
  },
  small: {
    padding: '6px 14px',
    fontSize: 13,
    borderRadius: 6,
  },
  pill: {
    padding: '6px 16px',
    fontSize: 12,
    borderRadius: 100,
  },
};

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'sage',
  size = 'default',
  onClick,
  disabled = false,
  style,
  type = 'button',
}) => {
  const [pressed, setPressed] = useState(false);

  const handleMouseDown = useCallback(() => setPressed(true), []);
  const handleMouseUp = useCallback(() => setPressed(false), []);
  const handleMouseLeave = useCallback(() => setPressed(false), []);

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontFamily: "'Geist', sans-serif",
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'transform 100ms ease, opacity 150ms ease',
        transform: pressed && !disabled ? 'scale(0.985)' : 'scale(1)',
        whiteSpace: 'nowrap',
        ...variantStyles[variant],
        ...sizeStyles[size],
        ...style,
      }}
    >
      {children}
    </button>
  );
};
